/**
 * Cross-browser, iframe-safe blob downloader.
 *
 * Why this exists:
 *   Many places in the app were doing
 *       const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
 *       a.download = "..."; a.click();
 *   which silently fails in:
 *     1. The Emergent preview iframe (un-attached anchors are blocked)
 *     2. Firefox (anchor must be in the DOM for the `download` attribute to work)
 *     3. Strict CSP / sandboxed previews
 *   On top of that, none of the call-sites validated `r.ok`, so any 401/403/500
 *   from the backend just produced a silent no-op.
 *
 * This util centralises the fix:
 *   - injects the Authorization header from localStorage
 *   - validates the response & surfaces a clear toast on failure
 *   - appends the anchor to the DOM before clicking
 *   - if the download attribute isn't honoured (rare iframe case), falls back
 *     to `window.open(url, "_blank")` so the file at least opens in a new tab
 *   - schedules URL.revokeObjectURL after 1s so we don't race the download
 */
import { toast } from "sonner";
import { API } from "@/lib/api";

function _filenameFromHeaders(res, fallback) {
  const cd = res.headers.get("content-disposition") || "";
  const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(cd);
  return (m && decodeURIComponent(m[1])) || fallback;
}

/**
 * Download a file from a backend endpoint as a blob.
 *
 * @param {Object} opts
 * @param {string} opts.url         Full URL or path-with-leading-slash (e.g. "/reports/complaints?fmt=pdf")
 * @param {string} [opts.filename]  Suggested filename (used if server doesn't provide Content-Disposition)
 * @param {function} [opts.onStart] Called with true when fetch starts, false when finished
 * @param {function} [opts.onError] Optional override; defaults to a sonner toast
 */
export async function downloadBlob({ url, filename = "download", onStart, onError } = {}) {
  if (onStart) onStart(true);
  // Resolve the URL exactly like axios does in `lib/api.js`:
  //   - absolute http(s) URLs are used as-is (rare; reports module sometimes does this)
  //   - relative paths are prefixed with the shared API base.
  // The shared API base is "/api" in production / preview (same-origin) and only
  // becomes `${REACT_APP_BACKEND_URL}/api` for true cross-origin localhost dev.
  // This means a wrong baked-in REACT_APP_BACKEND_URL can NEVER break downloads
  // in any deployed environment.
  const fullUrl = (() => {
    if (/^https?:\/\//.test(url)) return url;
    // Tolerate both conventions: "/master-export/foo" AND "/api/master-export/foo".
    if (url.startsWith("/api/") || url === "/api") {
      // Strip the leading "/api" so we don't get "/api/api/...".
      return url; // already absolute path from page root — fetch resolves against same origin.
    }
    return `${API}${url.startsWith("/") ? "" : "/"}${url}`;
  })();
  const token = localStorage.getItem("access_token");
  try {
    const res = await fetch(fullUrl, {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const ct = res.headers.get("content-type") || "";
        if (ct.includes("application/json")) {
          const j = await res.json();
          if (j && (j.detail || j.message)) detail = j.detail || j.message;
        } else {
          // Non-JSON error body — keep the status text so the user knows what happened.
          const t = (await res.text()).slice(0, 200);
          if (t) detail = `HTTP ${res.status} — ${t}`;
        }
      } catch { /* keep status code */ }
      const msg = res.status === 401 ? "Not authorised — please log in again"
                 : res.status === 403 ? "You don't have permission for this export"
                 : "Export failed";
      if (onError) onError(detail); else toast.error(msg, { description: String(detail) });
      return false;
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const serverName = _filenameFromHeaders(res, filename);

    // Primary path: in-DOM anchor click with `download` attribute.
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = objectUrl;
    a.download = serverName;
    a.rel = "noopener";
    document.body.appendChild(a);
    let clicked = false;
    try {
      a.click();
      clicked = true;
    } catch { /* fall through to fallback */ }

    // Fallback: if click was blocked (sandboxed iframe), open in a new tab so
    // the user can still save the file via the browser's native viewer.
    if (!clicked) {
      const w = window.open(objectUrl, "_blank", "noopener");
      if (!w) {
        toast.message("Pop-up blocked", {
          description: "Allow pop-ups for this site to download the file.",
        });
      }
    }

    // Cleanup after the browser has had time to start the download.
    setTimeout(() => {
      try { document.body.removeChild(a); } catch { /* already gone */ }
      URL.revokeObjectURL(objectUrl);
    }, 1500);

    return true;
  } catch (err) {
    // `fetch` throws a TypeError with message "Failed to fetch" for network/CORS
    // errors. Translate that into something actionable instead of just relaying
    // the cryptic browser message.
    const isNetwork = err && (err.name === "TypeError"
                              || /Failed to fetch|NetworkError|Network request failed/i.test(err.message || ""));
    const msg = isNetwork ? "Cannot reach the server" : "Export failed";
    const description = isNetwork
      ? `Tried ${fullUrl}. Check that the backend is reachable from this domain.`
      : (err && err.message) || "Unknown error";
    if (onError) onError(description); else toast.error(msg, { description });
    return false;
  } finally {
    if (onStart) onStart(false);
  }
}
